import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";
import { NAV_GROUPS } from "@/config/nav";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function Sidebar() {
  const { sidebarCollapsed } = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "fixed left-0 top-0 bottom-0 z-40 flex flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-[width] duration-200 ease-in-out",
          sidebarCollapsed ? "w-[72px]" : "w-[260px]",
        )}
      >
        {/* Brand Header */}
        <div
          className={cn(
            "flex items-center border-b border-sidebar-border shrink-0",
            sidebarCollapsed ? "h-[60px] justify-center px-0" : "h-[60px] px-5",
          )}
        >
          {sidebarCollapsed ? (
            <span className="text-lg font-black tracking-widest text-[#c3022d]">E</span>
          ) : (
            <span className="text-base font-black tracking-[0.2em] text-sidebar-foreground">
              END<span className="text-[#c3022d]">EKS</span>
            </span>
          )}
        </div>

        {/* Scrollable nav area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-3">
          {NAV_GROUPS.map((group) => (
            <div key={group.title} className="mb-1">
              {/* Group title */}
              {!sidebarCollapsed && group.title && (
                <p className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/40 select-none">
                  {group.title}
                </p>
              )}
              {sidebarCollapsed && group.title && (
                <div className="mx-3 my-1 h-px bg-sidebar-border/50" />
              )}

              {/* Nav items */}
              <ul className="space-y-0.5 px-2">
                {group.items.map((item) => {
                  const isActive =
                    item.to === "/"
                      ? location.pathname === "/"
                      : location.pathname === item.to ||
                        location.pathname.startsWith(item.to + "/");

                  const Icon = item.icon;

                  const itemButton = (
                    <button
                      type="button"
                      onClick={() => navigate(item.to)}
                      className={cn(
                        "relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 group",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                        sidebarCollapsed && "justify-center px-0",
                      )}
                    >
                      {/* Active left accent bar */}
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-[#c3022d]" />
                      )}
                      <Icon
                        className={cn(
                          "shrink-0",
                          isActive
                            ? "text-[#c3022d]"
                            : "text-sidebar-foreground/50 group-hover:text-sidebar-accent-foreground",
                        )}
                        size={18}
                      />
                      {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                    </button>
                  );

                  return (
                    <li key={item.to}>
                      {sidebarCollapsed ? (
                        <Tooltip>
                          <TooltipTrigger asChild>{itemButton}</TooltipTrigger>
                          <TooltipContent side="right" className="font-medium">
                            {item.label}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        itemButton
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </aside>
    </TooltipProvider>
  );
}
